import React from "react";
import { Card } from "react-bootstrap";
import ReportTableFrame from "@core/components/ReportTableFrame";
import type { PivotEntity, PivotMeasureRow, PivotModel } from "@core/reports/adapters/financePivotAdapters";

/**
 * Custom pivot table for the income views (Tab 1 / Tab 2 at district & nation).
 * Dynamic KPI columns × measure leaf rows, District→Church hierarchy with
 * frontend-computed subtotal + grand-total rows. Read-only (no per-church controls).
 * Shares the report-table skin (`.ntcog-report-*`) with ReportTreeTable.
 */

type Props = {
  title: string;
  model: PivotModel;
};

function money(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const IncomePivotTable: React.FC<Props> = ({ title, model }) => {
  const { columns, entities, grandTotal } = model;
  const colCount = columns.length + 1;

  const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set(entities.map((e) => e.id)));
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const measureRows = (rows: PivotMeasureRow[], indent: number, keyPrefix: string) =>
    rows.map((r) => (
      <tr key={`${keyPrefix}:${r.key}`} data-depth={indent}>
        <td style={{ paddingLeft: 16 + indent * 16 }}>{r.label}</td>
        {columns.map((c) => (
          <td key={c.kpi_id}>{money(r.values[c.kpi_id])}</td>
        ))}
      </tr>
    ));

  const entityHeaderRow = (entity: PivotEntity, indent: number) => {
    const isOpen = expanded.has(entity.id);
    return (
      <tr key={`${entity.id}:hdr`} data-depth={indent} className="ntcog-rt-row--header">
        <td colSpan={colCount} style={{ paddingLeft: 8 + indent * 16 }}>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="ntcog-rt-expander"
              aria-expanded={isOpen}
              aria-label={`${isOpen ? "Collapse" : "Expand"} ${entity.name}`}
              onClick={() => toggle(entity.id)}
            >
              {isOpen ? "▾" : "▸"}
            </button>
            <span>{entity.name}</span>
          </div>
        </td>
      </tr>
    );
  };

  // Build the body rows imperatively into a flat keyed array (avoids nested keyed
  // React.Fragment typing issues and keeps the <tbody> a single child list).
  const body: React.ReactNode[] = [];
  for (const entity of entities) {
    const isOpen = expanded.has(entity.id);
    body.push(entityHeaderRow(entity, 0));
    if (!isOpen) continue;
    if (entity.level === "church") {
      body.push(...measureRows(entity.measureRows, 1, entity.id));
    } else {
      for (const child of entity.children ?? []) {
        body.push(entityHeaderRow(child, 1));
        if (expanded.has(child.id)) body.push(...measureRows(child.measureRows, 2, child.id));
      }
      body.push(
        <tr className="ntcog-rt-row--total" key={`${entity.id}:dtot`}>
          <td style={{ paddingLeft: 24 }} className="fst-italic">District Total</td>
          {columns.map((c) => <td key={c.kpi_id} />)}
        </tr>
      );
      body.push(...measureRows(entity.measureRows, 2, `${entity.id}:subtotal`));
    }
  }
  if (entities.length > 0) {
    body.push(
      <tr className="ntcog-rt-row--total" key="grand:hdr">
        <td>Grand Total</td>
        {columns.map((c) => <td key={c.kpi_id} />)}
      </tr>
    );
    body.push(...measureRows(grandTotal, 1, "grand"));
  }

  return (
    <Card className="ntcog-card">
      <Card.Header className="fw-semibold">{title}</Card.Header>
      <Card.Body className="p-0">
        <ReportTableFrame>
          <table className="ntcog-report-table" style={{ minWidth: Math.max(480, colCount * 110) }}>
            <thead>
              <tr>
                <th scope="col"></th>
                {columns.map((c) => (
                  <th key={c.kpi_id} scope="col">{c.source}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entities.length === 0 ? (
                <tr><td colSpan={colCount} className="text-center text-muted py-3">No income recorded for this period.</td></tr>
              ) : null}
              {body}
            </tbody>
          </table>
        </ReportTableFrame>
      </Card.Body>
    </Card>
  );
};

export default IncomePivotTable;
